import React, { useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { WarningOutlined } from '@ant-design/icons'
import { Modal, Pagination, Spin, Tooltip, Typography } from 'antd'
import { chunk, get, isEmpty, isNil, map, round, size, toNumber } from 'lodash'
import PropTypes from 'prop-types'

import { GridFormItem } from '../../../core/components/sections'
import { showErrorNotification } from '../../../core/helpers'

import { StyledPaddingBottomRow } from '../../../common/components/styled'

import { PRIORITY_TASK_ALLOCATION_TYPE } from '../../constants'
import {
  useBrandsWithNoTop200MenuLazyQuery,
  usePriorityTaskMenuAllocationCountsQuery,
} from '../../hooks'

const PriorityTaskMenuCurationCounts = ({ priorityTask }) => {
  const [isBrandsModalVisible, setIsBrandsModalVisible] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)
  const { data, isLoading, error } = usePriorityTaskMenuAllocationCountsQuery(
    priorityTask.id,
  )

  const [fetchBrandsWithNoTop200Menu, { data: brandsWithNoTop200Menu }] =
    useBrandsWithNoTop200MenuLazyQuery()

  useEffect(() => {
    if (
      priorityTask.allocationType ===
      PRIORITY_TASK_ALLOCATION_TYPE.TOP_200.value
    )
      fetchBrandsWithNoTop200Menu()
  }, [fetchBrandsWithNoTop200Menu, priorityTask.allocationType])

  const { menuCurationTasks } = useSelector(
    store => store.priorityTasks.priorityTask,
  )

  useEffect(() => {
    if (error) {
      showErrorNotification({
        title: 'Failed to get menu counts',
        description: error,
      })
    }
  }, [error])

  const timeRemainingHours = useMemo(() => {
    if (isNil(priorityTask?.kpi)) {
      return null
    }
    const kpiMinutesPerDish = toNumber(priorityTask.kpi)
    const availableDishes = get(data, 'availableDishCount')
    if (isNil(availableDishes)) {
      return null
    }
    const totalMinutes = kpiMinutesPerDish * toNumber(availableDishes)
    return totalMinutes / 60
  }, [priorityTask?.kpi, data])

  const showTop200BrandsWarning = useMemo(
    () =>
      priorityTask?.allocationType ===
        PRIORITY_TASK_ALLOCATION_TYPE.TOP_200.value &&
      !isEmpty(brandsWithNoTop200Menu),
    [priorityTask?.allocationType, brandsWithNoTop200Menu],
  )

  const top200BrandPages = useMemo(
    () => chunk(brandsWithNoTop200Menu || [], 20),
    [brandsWithNoTop200Menu],
  )

  const currentBrandsPage = useMemo(
    () => get(top200BrandPages, currentPage - 1, []),
    [top200BrandPages, currentPage],
  )

  if (isLoading) {
    return <Spin />
  }

  if (!data) {
    return null
  }

  return (
    <StyledPaddingBottomRow gutter={16}>
      <GridFormItem span={3} label="Current selection">
        {menuCurationTasks?.total}
      </GridFormItem>
      <GridFormItem span={3} label="Available menus">
        <div>
          <span>
            {data.availableMenusCount}{' '}
            {showTop200BrandsWarning && <WarningOutlined />}
          </span>
        </div>
        {showTop200BrandsWarning && (
          <div>
            <Typography.Link onClick={() => setIsBrandsModalVisible(true)}>
              Show missing brands
            </Typography.Link>
          </div>
        )}
      </GridFormItem>
      <GridFormItem span={3} label="Allocated menus">
        {data.allocatedMenusCount}
      </GridFormItem>
      <GridFormItem span={3} label="Done menus">
        {data.doneMenusCount}
      </GridFormItem>
      <GridFormItem span={3} label="Available dishes">
        <Tooltip
          title={
            isNil(data.availableDishCount)
              ? 'This feature is not available for CHAD tasks'
              : null
          }
        >
          {isNil(data.availableDishCount) ? 'N/A' : data.availableDishCount}
        </Tooltip>
      </GridFormItem>
      <GridFormItem span={3} label="Allocated dishes">
        {isNil(data.allocatedDishCount) ? 'N/A' : data.allocatedDishCount}
      </GridFormItem>
      <GridFormItem span={3} label="Done dishes">
        {isNil(data.doneDishCount) ? 'N/A' : data.doneDishCount}
      </GridFormItem>
      {!isNil(priorityTask.kpi) && (
        <GridFormItem span={3} label="Time remaining">
          {isNil(timeRemainingHours)
            ? 'N/A'
            : `${round(timeRemainingHours, 2)} Hours`}
        </GridFormItem>
      )}
      <Modal
        title="Top 200 Brands still waiting for a menu from Autoeat"
        visible={isBrandsModalVisible}
        onCancel={() => setIsBrandsModalVisible(false)}
        footer={null}
      >
        {map(currentBrandsPage, brand => (
          <div key={brand.id}>{brand.name}</div>
        ))}
        <Pagination
          style={{ marginTop: 16, textAlign: 'right' }}
          current={currentPage}
          pageSize={20}
          total={size(brandsWithNoTop200Menu)}
          onChange={setCurrentPage}
        />
      </Modal>
    </StyledPaddingBottomRow>
  )
}

PriorityTaskMenuCurationCounts.propTypes = {
  priorityTask: PropTypes.object.isRequired,
}

export default PriorityTaskMenuCurationCounts
